import { useCurrentUserStore } from '@/stores/useCurrentUserStore'
import { useSignalSampleMode } from '@/features/signal/composables/useSignalSampleMode'
import { useAnalytics } from '@/composables/useAnalytics'

export function useSignalAPI() {
    const { analytics } = useAnalytics()
    const { isSample } = useSignalSampleMode()

    async function baseFetch(path, options = {}) {
        const key = getSignalKey()

        if (!key) {
            console.error('Signal Access key required')
        }

        if (options.body) {
            options.headers = {
                ...options.headers,
                'content-type': 'application/json',
            }

            options.body = JSON.stringify(options.body)
        }

        // Extract signal if present
        const { signal, ...restOptions } = options

        return await fetch(`${import.meta.env.VITE_API_URL_NEURON}${path}`, {
            ...restOptions,
            signal, // Pass signal to fetch for cancellation
            headers: {
                ...restOptions.headers,
                accept: 'application/json',
                'x-api-key': key,
            },
        })
    }

    async function api(path, options = {}) {
        // options.signal can be passed in for cancellation
        const response = await baseFetch(path, options)

        if (!response.ok) {
            const errorText = await response.text()

            throw new Error(errorText)
        }

        try {
            return await response.json()
        } catch (e) {
            analytics.captureException(e)

            return null
        }
    }

    function urlWithKey(path) {
        const url = new URL(`${import.meta.env.VITE_API_URL_NEURON}${path}`)
        const key = getSignalKey()
        url.searchParams.append('x-api-key', key)
        return url
    }

    function getSignalKey() {
        if (isSample.value) {
            return import.meta.env.VITE_SIGNAL_SAMPLE_API_KEY
        }

        const currentUser = useCurrentUserStore()

        if (!currentUser.has.signal) return null

        const { key } = currentUser.products.signal.record

        return key
    }

    function removeEmptyParams(obj) {
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([, v]) => v != null)
                .map((item) => convertArray(item))
        )
    }

    function convertArray(item) {
        const [prop, value] = item

        if (!Array.isArray(value)) return item

        return [prop, value.join(',')]
    }

    async function relationshipsBaseFetch(path, options = {}) {
        const key = getSignalKey()

        if (!key) {
            throw new Error('Signal access key required')
        }

        if (options.body) {
            options.headers = {
                ...options.headers,
                'content-type': 'application/json',
            }

            options.body = JSON.stringify(options.body)
        }

        // Extract signal if present
        const { signal, ...restOptions } = options

        return await fetch(`${import.meta.env.VITE_API_URL_PROVIDER_DIRECTORY}${path}`, {
            ...restOptions,
            signal, // Pass signal to fetch for cancellation
            headers: {
                ...restOptions.headers,
                accept: 'application/json',
                'x-api-key': key,
            },
        })
    }

    async function getRelationships(path = '', params = {}, options = {}) {
        const cleanParams = removeEmptyParams(params)
        const queryString = new URLSearchParams(cleanParams).toString()
        const fullPath = `${path}${queryString ? `?${queryString}` : ''}`

        const response = await relationshipsBaseFetch(fullPath, options)

        if (!response.ok) {
            const errorText = await response.text()

            throw new Error(errorText)
        }

        try {
            return await response.json()
        } catch (e) {
            analytics.captureException(e)

            return null
        }
    }

    return {
        api,
        removeEmptyParams,
        urlWithKey,
        getSignalKey,
        getRelationships,
        relationshipsBaseFetch,
    }
}
